import * as React from 'react'
import { cn } from '@/lib/utils'

type PageWidth = 'default' | 'wide'

/**
 * The one place a page's body width is decided, wired to the design-system
 * content-width tokens so every page is consistent instead of picking ad-hoc
 * `max-w-*` values:
 *
 * - `default` (`--ds-content-width`, 54rem) — reading/form pages (detail, create).
 *   Capped hard, because a line of prose past this measure is tiring to read.
 * - `wide` — dense tables, boards, lists and image grids. Fluid: it fills the
 *   shell and keeps growing on a bigger display up to `--ds-content-width-max`,
 *   rather than stopping at 72rem and banking the rest as empty margin. Content
 *   that benefits from width should get the width; gutters are not a design.
 *
 * Below the cap the body is full-width (minus the shell's padding); above it the
 * column centers. Every AppLayout page should render its content inside one of
 * these rather than setting its own max-width.
 */
export function PageContainer({
  width = 'default',
  className,
  children,
}: {
  width?: PageWidth
  className?: string
  children: React.ReactNode
}) {
  return (
    <div
      className={cn(
        'mx-auto w-full',
        width === 'wide'
          ? 'max-w-[min(100%,var(--ds-content-width-max))]'
          : 'max-w-[var(--ds-content-width)]',
        className,
      )}
    >
      {children}
    </div>
  )
}
